/* eslint-disable react/no-unknown-property */
import React, { useState, useContext } from "react";
import moment from "moment";
import { ProjectContext } from "GlobalState";
import styled from "styled-components";
import { Col, Row, Button, Card, CardHeader, CardBody } from "reactstrap";
import ReactToPrint from "react-to-print";
import { useQuery } from "@apollo/client";
import { TEAM_QUERY } from "../../graphql/queries";
import UserHelper from "../../UserHelper";
import { getCurrentDate } from "../../utilities/utils";
import splLogo from "../../static/images/message/spl-logo.jpg";

const PrintDiv = styled.div`
  @media print {
    @page {
      size: landscape;
    }
    padding: 50px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  border-collapse: separate;
  border-spacing: 0 5px;

  th,
  td {
    border: 1px solid #ddd;
    padding: 25px 12px; /* Increased padding for better spacing */
    text-align: center;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
    text-transform: uppercase; /* Make text uppercase */
  }

  td.ranking {
    font-weight: bold; /* Apply bold font to ranking cells */
  }

  td.category {
    text-decoration: underline;
    text-transform: uppercase;
  }
`;

const AgileScorecard = ({ currentUser }) => {
  const [elementRef, setElementRef] = useState(null);
  const [state] = useContext(ProjectContext);
  const today = new Date();
  const date = `${today.getFullYear()}-${today.getMonth() +
    1}-${today.getDate()}`;
  const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
  // eslint-disable-next-line no-unused-vars
  let documentTitle = null;

  const afterPrint = () => {
    documentTitle = document.title;
  };

  const beforePrint = newTitle => {
    documentTitle = newTitle;
  };

  const { data, loading, error } = useQuery(TEAM_QUERY, {
    variables: { teamId: state.currentTeamId }
  });

  if (loading) return <p>Loading team data...</p>;
  if (error) return <p>Error loading team data.</p>;

  const teamData = data?.team;

  if (teamData.currentStatus !== "COMPLETED") {
    return <p>This report will be available at the end of the run</p>;
  }

  const translateRanking = ranking => {
    switch (ranking) {
      case "PROFICIENCY":
        return "READY TO PERFORM";
      case "NEEDS_IMPROVEMENT":
        return "BEGINNING";
      case "DEVELOPING":
        return "ADVANCING";
      default:
        return ranking;
    }
  };

  const getRowStyle = ranking => {
    switch (ranking) {
      case "PROFICIENCY":
        return { backgroundColor: "#AEC8F6" }; // Light Blue
      case "NEEDS_IMPROVEMENT":
        return { backgroundColor: "#E69992" }; // Light Red
      case "DEVELOPING":
        return { backgroundColor: "#B0E1CA" }; // Light Green
      default:
        return {};
    }
  };

  const comboChartHeader = {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column"
  };

  const renderDate = dateString => {
    if (dateString === null || dateString === "") {
      return <span />;
    }
    if (Date.parse(dateString) < 0) {
      return <span />;
    }
    // eslint-disable-next-line no-shadow
    const date = new Date(dateString);
    // eslint-disable-next-line no-undef
    return <span>{moment(date).format("DD-MMM-YYYY")}</span>;
  };

  return (
    <Card>
      <CardHeader>
        <Row>
          <Col md="3"></Col>
          <Col>
            <span className="float-sm-right">
              <ReactToPrint
                trigger={() => (
                  <Button color="secondary">
                    <i className="far fa-file-pdf" /> Print
                  </Button>
                )}
                content={() => elementRef}
                onBeforePrint={() => beforePrint("Agile Scorecard")}
                onAfterPrint={() => afterPrint()}
                documentTitle={`${("Agile Scorecard", "_")}${
                  currentUser.firstName
                }${currentUser.lastName}_${date}_${time}_${UserHelper.teamId(
                  currentUser,
                  state
                )}`}
              />
            </span>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        <PrintDiv
          id="h123"
          ref={el => {
            setElementRef(el);
          }}
        >
          <Row>
            <Col>
              <span>{`${currentUser.firstName} ${currentUser.lastName}`}</span>
              <br />
              <span>Run {state.runSequenceNumber} </span>
              <br />
              <span>{renderDate(getCurrentDate())}</span>
              <br />
              <br />
            </Col>
            <Col>
              <h2 style={comboChartHeader}>Agile Scorecard</h2>
            </Col>
            <Col className="text-center text-md-right">
              <img src={splLogo} style={{ width: "40%" }} alt="spl logo" />
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md="12" className="text-center">
              {teamData ? (
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Score</th>
                      <th>Proficiency Level</th>
                      <th>Feedback</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={getRowStyle(teamData.agileCoachingScoreRanking)}>
                      <td className="category">
                        Proficiency in Facilitating Scrum Ceremonies
                      </td>
                      <td>{teamData.agileCoachingScore ?? "N/A"}/12</td>
                      <td
                        className="ranking"
                        ranking={translateRanking(
                          teamData.agileCoachingScoreRanking
                        )}
                      >
                        {translateRanking(teamData.agileCoachingScoreRanking) ??
                          "N/A"}
                      </td>
                      <td>
                        {teamData.agileCoachingScoreRanking ===
                          "PROFICIENCY" && (
                          <span>
                            Your sprint planning clearly sets team goals and
                            drives engagement, resulting in focused and
                            effective Scrum ceremonies.
                          </span>
                        )}
                        {teamData.agileCoachingScoreRanking ===
                          "DEVELOPING" && (
                          <span>
                            Your sprint planning meets basic needs, but a more
                            structured agenda and clear objectives can boost
                            team alignment and ceremony impact.
                          </span>
                        )}
                        {teamData.agileCoachingScoreRanking ===
                          "NEEDS_IMPROVEMENT" && (
                          <span>
                            Your sprint planning lacks clarity and structure,
                            leading to misaligned Scrum ceremonies. Enhance
                            agenda setting and proactively address blockers for
                            better outcomes.
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr
                      style={getRowStyle(teamData.scrumCeremoniesScoreRanking)}
                    >
                      <td className="category">
                        Mastery in Agile Team Coaching
                      </td>
                      <td>{teamData.scrumCeremoniesScore ?? "N/A"}/27</td>
                      <td
                        className="ranking"
                        ranking={translateRanking(
                          teamData.scrumCeremoniesScoreRanking
                        )}
                      >
                        {translateRanking(
                          teamData.scrumCeremoniesScoreRanking
                        ) ?? "N/A"}
                      </td>
                      <td>
                        {teamData.scrumCeremoniesScoreRanking ===
                          "PROFICIENCY" && (
                          <span>
                            Your agile team coaching is exemplary. You actively
                            support team members in adopting self-management and
                            collaborative problem-solving, offering clear,
                            actionable feedback. Your approach creates an
                            environment of continuous learning and improvement
                            that drives high performance.
                          </span>
                        )}
                        {teamData.scrumCeremoniesScoreRanking ===
                          "DEVELOPING" && (
                          <span>
                            You have a solid foundation in agile team coaching,
                            and your efforts to encourage growth in team members
                            are evident. To further enhance your impact, try
                            customizing your coaching strategies to address
                            individual challenges and promote deeper reflection.
                            More opportunities for one-on-one guidance could
                            help solidify your effectiveness.
                          </span>
                        )}
                        {teamData.scrumCeremoniesScoreRanking ===
                          "NEEDS_IMPROVEMENT" && (
                          <span>
                            Enhance your agile coaching by investing time to
                            understand your team members' unique strengths and
                            challenges, and tailor your approach to empower
                            their independence.
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr style={getRowStyle(teamData.mvpStoriesScoreRanking)}>
                      <td className="category">Delivering MVP Stories</td>
                      <td>{teamData.mvpStoriesScore ?? "N/A"}%</td>
                      <td
                        className="ranking"
                        ranking={translateRanking(
                          teamData.mvpStoriesScoreRanking
                        )}
                      >
                        {translateRanking(teamData.mvpStoriesScoreRanking) ??
                          "N/A"}
                      </td>
                      <td>
                        {teamData.mvpStoriesScoreRanking === "PROFICIENCY" && (
                          <span>
                            You have achieved delivery of MVP stories convert
                            business needs into clear, actionable tasks that
                            align with sprint goals and earn stakeholder
                            approval.
                          </span>
                        )}
                        {teamData.mvpStoriesScoreRanking === "DEVELOPING" && (
                          <span>
                            Your stories capture user needs and sprint
                            objectives, but enhancing team collaboration and
                            check-ins could improve alignment.
                          </span>
                        )}
                        {teamData.mvpStoriesScoreRanking ===
                          "NEEDS_IMPROVEMENT" && (
                          <span>
                            Strengthen your MVP stories by linking them more
                            closely to sprint planning and team capacity, and
                            increase regular check-ins to minimize blockers.
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </StyledTable>
              ) : (
                <p>Loading team data...</p>
              )}
            </Col>
          </Row>
        </PrintDiv>
      </CardBody>
    </Card>
  );
};

export default AgileScorecard;
